import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck, Upload, Download, XCircle, FileSignature, AlertTriangle } from "lucide-react";

/**
 * DSC Signing Panel — Route (d): user signs the document on their own machine
 * with a DSC Class 3 token, then uploads the signed file back here.
 *
 * Props:
 *   docType    "sales_invoice" | "purchase_order"
 *   docId      string
 *   downloadUnsignedUrl   URL to download the unsigned PDF (Zenith-generated)
 *   downloadUnsignedLabel optional label for the download button
 *   onSigned   callback fired after a successful upload (parent can refresh)
 */
export default function DSCSigningPanel({ docType, docId, downloadUnsignedUrl, downloadUnsignedLabel = "Download unsigned PDF", onSigned }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/dsc/status/${docType}/${docId}`);
      setStatus(r.data);
    } catch (e) { setStatus({ signed: false }); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (docId) load(); }, [docId]);

  const upload = async (evt) => {
    const f = evt.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/dsc/upload/${docType}/${docId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.metadata_parsed) {
        toast.success(`Signed by ${r.data.signer_name || "(name in cert)"} — locked from further edits.`);
      } else {
        toast.success("Signed file uploaded — document locked. (Signer name not parseable from this format.)");
      }
      await load();
      onSigned?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async () => {
    try {
      const r = await api.get(`/dsc/download/${docType}/${docId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = status?.filename || "signed.bin";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const clearSig = async () => {
    if (!confirm("Clear the digital signature? The document will become editable again.")) return;
    try {
      await api.delete(`/dsc/clear/${docType}/${docId}`);
      toast.success("Signature cleared. Document is editable again.");
      load();
      onSigned?.(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
  };

  if (busy) return <Card className="p-4 rounded-sm text-sm text-muted-foreground">Loading signature status…</Card>;

  if (status?.signed) {
    return (
      <Card className="p-4 rounded-sm border-2 border-emerald-300 bg-emerald-50/60" data-testid={`dsc-panel-signed-${docType}-${docId}`}>
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Digitally Signed · Class 3 DSC</div>
            <div className="font-semibold text-emerald-900 truncate">{status.signer_name || "Signature file uploaded (signer name not in metadata)"}</div>
            <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
              {status.signer_issuer && <div>Issued by: {status.signer_issuer}</div>}
              {status.cert_serial && <div className="font-mono">Serial: {status.cert_serial}</div>}
              {status.signing_time && <div>Signed at: {status.signing_time}</div>}
              <div>File: {status.filename} · {(status.size_bytes / 1024).toFixed(1)} KB · Uploaded {new Date(status.uploaded_at).toLocaleString("en-IN")} by {status.uploaded_by_name}</div>
              {status.sha256 && <div className="font-mono truncate">SHA-256: {status.sha256.slice(0, 32)}…</div>}
            </div>
            <div className="text-[11px] text-emerald-800 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Document is now read-only. Clear the signature to re-open for edits.</div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={download} variant="outline" className="rounded-sm gap-1.5" data-testid={`dsc-download-signed-${docType}-${docId}`}>
                <Download className="w-3.5 h-3.5" /> Download signed
              </Button>
              <Button size="sm" onClick={clearSig} variant="outline" className="rounded-sm gap-1.5 text-red-700 border-red-200 hover:bg-red-50" data-testid={`dsc-clear-${docType}-${docId}`}>
                <XCircle className="w-3.5 h-3.5" /> Clear signature
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 rounded-sm border-2 border-dashed border-accent/40 bg-accent/5" data-testid={`dsc-panel-unsigned-${docType}-${docId}`}>
      <div className="flex items-start gap-3">
        <FileSignature className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Digital signature · Class 3 DSC</div>
          <div className="font-semibold">Sign this document with your DSC token</div>
          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
            <div>1. Download the unsigned PDF below.</div>
            <div>2. Open it in Adobe Acrobat → <i>Tools → Certificates → Digitally Sign</i> (or use MCA emSigner).</div>
            <div>3. Sign with your Class 3 DSC USB token.</div>
            <div>4. Upload the signed file back here to lock the document.</div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {downloadUnsignedUrl && (
              <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(downloadUnsignedUrl, "_blank")} data-testid={`dsc-download-unsigned-${docType}-${docId}`}>
                <Download className="w-3.5 h-3.5" /> {downloadUnsignedLabel}
              </Button>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.p7s,.p7m,.cms,.json,.xml" onChange={upload} className="hidden" data-testid={`dsc-file-input-${docType}-${docId}`} />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid={`dsc-upload-btn-${docType}-${docId}`}>
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload signed file"}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
